import type { FaqItem } from '@/routes/new/-components/faq-accordion';
import { faqEntriesCa } from './faq/ca';
import { faqEntriesEn } from './faq/en';
import { faqEntriesEs } from './faq/es';
import { faqEntriesFr } from './faq/fr';
import { faqEntriesIt } from './faq/it';

/**
 * The FAQ page's content, one module per locale.
 *
 * NOT in the Lingui catalogs, unlike every other string on the redesigned site, and the reason is the
 * lint gate: `lingui compile --strict` runs inside `pnpm lint` and fails on an empty `msgstr` in ANY
 * of the ten catalogs. The FAQ is ~50 questions, so putting it there would make the ten-language
 * translation a precondition for compiling, for a page the site publishes in five. The legal pages
 * already keep their text out of the catalogs for the same reason (`legal-content.ts`); this follows
 * that precedent, with the content as typed modules rather than markdown because the accordion and the
 * `FAQPage` structured data need the questions as data, not as a blob of HTML.
 *
 * TypeScript rather than JSON so `tsc` checks every `block` and every link `target`: a translator
 * cannot name a section or a destination that does not exist.
 */

/** The five sections the content is organised in, in the order the page shows them. */
export const FAQ_BLOCKS = ['companies', 'ngos', 'donors', 'transparency', 'about'] as const;

export type FaqBlock = (typeof FAQ_BLOCKS)[number];

/** A question, plus which section it belongs to. */
export type FaqEntry = FaqItem & { block: FaqBlock };

/**
 * One module per locale the redesign publishes (`newLocales`).
 *
 * Spanish is the SOURCE, not English — Comms wrote the document in Spanish and the other four are
 * translated from it, which is the opposite of the code, whose `msgid` is English. So a locale with no
 * module of its own falls back to Spanish rather than to English. Today every published locale has one;
 * the fallback covers a locale being published before its translation lands.
 */
const entriesByLocale: Partial<Record<string, FaqEntry[]>> = {
	ca: faqEntriesCa,
	en: faqEntriesEn,
	es: faqEntriesEs,
	fr: faqEntriesFr,
	it: faqEntriesIt,
};

export function getFaqEntries(locale: string): FaqEntry[] {
	return entriesByLocale[locale] ?? faqEntriesEs;
}
